import { useConvexQuery } from "@/hooks/use-convex-query"
import { useMemo } from "react"
import { CalendarClock } from "lucide-react"
import { motion } from "motion/react"
import { api } from "../../convex/_generated/api"
import {
  formatDateShort,
  isTemporaryHoursActive,
  parseTemporaryHours,
} from "@/lib/temporary-hours"

const sectionTransition = {
  duration: 0.6,
  ease: [0.22, 1, 0.36, 1] as const,
}

export function TemporaryHoursBanner() {
  const raw = useConvexQuery(api.settings.get, { key: "temporaryHours" }) as
    | string
    | null
    | undefined

  const data = useMemo(() => parseTemporaryHours(raw), [raw])

  const isActive = useMemo(() => {
    if (!data) return false
    return isTemporaryHoursActive(data)
  }, [data])

  if (!isActive || !data) return null

  const rangeLabel = `od ${formatDateShort(data.startDate)} do ${formatDateShort(data.endDate)}`

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-transparent via-zielarz-yellow/4 to-transparent py-16 sm:py-20">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zielarz-yellow/30 to-transparent" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={sectionTransition}
        className="mx-auto max-w-3xl px-6"
      >
        <div className="rounded-2xl border border-border/60 bg-background/60 p-6 shadow-sm backdrop-blur-sm sm:p-8">
          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-xl border border-zielarz-yellow/20 bg-zielarz-yellow/10 p-2.5">
              <CalendarClock className="size-5 text-zielarz-yellow" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight sm:text-xl">
                Tymczasowe godziny otwarcia
              </h2>
              <p className="text-sm text-muted-foreground">{rangeLabel}</p>
            </div>
          </div>

          {/* Intro */}
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Uwaga! W dniach {rangeLabel} obowiązują zmienione godziny otwarcia:
          </p>

          {/* Days grid */}
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.days.map((day) => {
              const isClosed = !day.opens || !day.closes
              return (
                <div
                  key={day.date}
                  className="flex items-center justify-between rounded-xl border border-border/40 bg-secondary/30 px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <span className="text-sm font-semibold">
                      {day.dayLabel}
                    </span>
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {formatDateShort(day.date)}
                    </span>
                  </div>
                  <span
                    className={`ml-3 shrink-0 text-sm font-medium ${isClosed ? "text-destructive" : "text-foreground"}`}
                  >
                    {isClosed ? "Zamknięte" : `${day.opens} - ${day.closes}`}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </motion.div>
    </section>
  )
}
